/**
 * PDF Review Store
 *
 * Manages review state with localStorage as an optimistic cache and
 * server-side PaperworkEvents as the persistent source of truth.
 */

const LS_PREFIX = 'paperwork-review'

export class ReviewStore {
  constructor(paperworkId, { reviewStateUrl, csrfToken, onSyncError, onSyncSuccess } = {}) {
    this.paperworkId = paperworkId
    this.reviewStateUrl = reviewStateUrl
    this.csrfToken = csrfToken
    this.onSyncError = onSyncError
    this.onSyncSuccess = onSyncSuccess
    this.state = new Map() // path → {checked_at, user_id, match_status}
    this.pendingWrites = []
    this.flushTimeout = null
  }

  /**
   * Hydrate from server then merge with localStorage.
   */
  async hydrate() {
    // Load from localStorage first (instant)
    this.loadFromLocalStorage()

    // Then fetch server state and merge
    if (this.reviewStateUrl) {
      try {
        const response = await fetch(this.reviewStateUrl, {
          headers: { 'Accept': 'application/json' }
        })
        if (response.ok) {
          const serverState = await response.json()
          this.mergeServerState(serverState)
        }
      } catch (e) {
        console.warn('[ReviewStore] Failed to fetch server state:', e)
      }
    }

    return this.state
  }

  /**
   * Mark a field as reviewed.
   */
  markReviewed(path, { matchStatus = 'matched', userId = null } = {}) {
    const entry = {
      checked_at: new Date().toISOString(),
      user_id: userId,
      match_status: matchStatus
    }

    this.state.set(path, entry)
    this.saveToLocalStorage(path, entry)
    this.queueServerWrite(path, entry)
  }

  /**
   * Unmark a field (remove review).
   */
  unmarkReviewed(path) {
    const entry = {
      checked_at: new Date().toISOString(),
      user_id: null,
      match_status: 'unreviewed'
    }

    this.state.delete(path)
    this.removeFromLocalStorage(path)
    this.queueServerWrite(path, entry)
  }

  /**
   * Mark multiple paths at once (for bulk operations).
   */
  markBulkReviewed(paths, { matchStatus = 'matched', userId = null } = {}) {
    const entries = []
    paths.forEach(path => {
      const entry = {
        checked_at: new Date().toISOString(),
        user_id: userId,
        match_status: matchStatus
      }
      this.state.set(path, entry)
      this.saveToLocalStorage(path, entry)
      entries.push({ path, ...entry })
    })
    this.queueBulkServerWrite(entries)
  }

  /**
   * Check if a path is reviewed.
   */
  isReviewed(path) {
    return this.state.has(path)
  }

  /**
   * Get review metadata for a path.
   */
  getEntry(path) {
    return this.state.get(path)
  }

  /**
   * Check if a path was reviewed as flagged/problematic.
   */
  isFlagged(path) {
    return this.state.get(path)?.match_status === 'flagged'
  }

  /**
   * Get all reviewed paths.
   */
  getReviewedPaths() {
    return new Set(this.state.keys())
  }

  /**
   * Get review entries keyed by path.
   */
  getReviewEntries() {
    return new Map(this.state)
  }

  /**
   * Get review progress: {reviewed, total, unmatched, flagged}
   */
  getProgress(totalFields, matchResults) {
    let reviewed = this.state.size
    let flagged = 0
    let unmatched = 0
    for (const entry of this.state.values()) {
      if (entry.match_status === 'flagged') flagged++
    }
    if (matchResults) {
      reviewed = 0
      for (const [path, result] of matchResults) {
        if (result.status === 'skipped') continue
        if (this.state.has(path)) reviewed++
        if (result.status === 'unmatched' && !this.state.has(path)) unmatched++
      }
    }
    return { reviewed, total: totalFields, unmatched, flagged }
  }

  // --- localStorage helpers ---

  loadFromLocalStorage() {
    try {
      const prefix = `${LS_PREFIX}:${this.paperworkId}:`
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(prefix)) {
          const path = key.slice(prefix.length)
          const data = JSON.parse(localStorage.getItem(key))
          this.state.set(path, data)
        }
      }
    } catch (e) {
      console.warn('[ReviewStore] localStorage read error:', e)
    }
  }

  saveToLocalStorage(path, entry) {
    try {
      const key = `${LS_PREFIX}:${this.paperworkId}:${path}`
      localStorage.setItem(key, JSON.stringify(entry))
    } catch (e) {
      console.warn('[ReviewStore] localStorage write error:', e)
    }
  }

  removeFromLocalStorage(path) {
    try {
      const key = `${LS_PREFIX}:${this.paperworkId}:${path}`
      localStorage.removeItem(key)
    } catch (e) {
      // ignore
    }
  }

  // --- Server sync ---

  mergeServerState(serverState) {
    if (!serverState || typeof serverState !== 'object') return

    Object.entries(serverState).forEach(([path, entry]) => {
      const local = this.state.get(path)
      if (!local || new Date(entry.checked_at) > new Date(local.checked_at)) {
        this.state.set(path, entry)
        this.saveToLocalStorage(path, entry)
      }
    })
  }

  queueServerWrite(path, entry) {
    this.pendingWrites.push({ path, ...entry })
    this.scheduleFlush()
  }

  queueBulkServerWrite(entries) {
    this.pendingWrites.push(...entries)
    this.scheduleFlush()
  }

  scheduleFlush() {
    if (this.flushTimeout) return
    this.flushTimeout = setTimeout(() => {
      this.flush()
      this.flushTimeout = null
    }, 1000)
  }

  async flush() {
    if (this.pendingWrites.length === 0) return
    if (!this.reviewStateUrl) return

    const writes = [...this.pendingWrites]
    this.pendingWrites = []

    try {
      const response = await fetch(this.reviewStateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken || getCSRFToken()
        },
        body: JSON.stringify({ reviews: writes })
      })
      if (!response.ok) throw new Error(`Review sync failed (${response.status})`)

      this.onSyncSuccess?.()
    } catch (e) {
      console.warn('[ReviewStore] Server write failed, will retry:', e)
      this.pendingWrites.unshift(...writes)
      this.onSyncError?.(e)
    }
  }

  /**
   * Clear all review state for this paperwork.
   */
  clearAll() {
    const prefix = `${LS_PREFIX}:${this.paperworkId}:`
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k))
    this.state.clear()
  }
}

function getCSRFToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

export default ReviewStore
